import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ShiftTimelineDraftHelperService } from '../../../services/shift-timeline-draft-helper/shift-timeline-draft-helper.service';
import { firstValueFrom } from 'rxjs';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  PublicationStatus,
  Shift,
  TimelineDateQueryParamKey,
} from '@wilson/interfaces';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { format } from 'date-fns';
import { DateTimeFormat } from '@wilson/utils';

@Component({
  selector: 'wilson-shift-timeline-draft-header',
  imports: [CommonModule, NzButtonModule, TranslateModule, NzTooltipModule],
  templateUrl: './shift-timeline-draft-header.component.html',
  styleUrls: ['./shift-timeline-draft-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftTimelineDraftHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly shiftTimelineDraftHelperService = inject(
    ShiftTimelineDraftHelperService,
  );
  private readonly shiftsService = inject(ShiftsService);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly modalService = inject(NzModalService);

  public isSaving = false;
  changedShiftIds$ = this.shiftTimelineDraftHelperService.changedShiftIds$;

  async ngOnInit(): Promise<void> {
    const dateRange = await firstValueFrom(
      this.shiftTimelineDraftHelperService.dateRange$,
    );
    if (!dateRange.start || !dateRange.end) {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      await this.goBackToShiftTimeline();
    }
  }

  async goBackToShiftTimeline(): Promise<void> {
    this.shiftTimelineDraftHelperService.resetSelectedShifts();
    const dateRange = await firstValueFrom(
      this.shiftTimelineDraftHelperService.dateRange$,
    );

    this.router.navigate(['shift-timeline'], {
      queryParams: {
        [TimelineDateQueryParamKey]: format(
          new Date(dateRange.start),
          DateTimeFormat.DatabaseDateFormat,
        ),
      },
    });
  }

  async saveDraft(numberOfChanges: number): Promise<void> {
    const changedShiftRecords = await firstValueFrom(
      this.shiftTimelineDraftHelperService.changedShiftRecords$,
    );
    const userModalResponse = await firstValueFrom(
      this.confirmSubmission(numberOfChanges).afterClose,
    );

    if (userModalResponse && Object.keys(changedShiftRecords).length > 0) {
      try {
        this.isSaving = true;
        this.cdRef.detectChanges();
        const shiftsToUpdate: Partial<Shift>[] = Object.entries(
          changedShiftRecords,
        ).map(([shiftId, { newUserId }]) => ({
          id: shiftId,
          userId: newUserId,
          publicationStatus: PublicationStatus.NotPublished,
        }));
        await firstValueFrom(
          this.shiftsService.updateShiftsBulk(shiftsToUpdate),
        );
        this.nzMessageService.success(
          this.translate.instant('popup.save.text'),
        );
        await this.goBackToShiftTimeline();
      } catch {
        this.isSaving = false;
        this.cdRef.detectChanges();
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
  }

  confirmSubmission(numberOfChanges: number): NzModalRef {
    const translateString =
      numberOfChanges > 1
        ? 'page.shift_timeline_draft_action_card.apply_multiple_changes_confirmation_modal.header_text'
        : 'page.shift_timeline_draft_action_card.apply_single_change_confirmation_modal.header_text';

    return this.modalService.confirm({
      nzTitle: this.translate.instant(translateString, {
        number: numberOfChanges,
      }),
      nzContent: this.translate.instant(
        'page.shift_timeline_draft_action_card.apply_changes_confirmation_modal.description_text',
      ),
      nzOkText: this.translate.instant('general.apply'),
      nzCancelText: this.translate.instant('general.cancel'),
      nzOkType: 'primary',
      nzClosable: false,
      nzOkDanger: false,
      nzOnOk: () => true,
    });
  }
}
